<script setup lang="ts">
import { computed, ref } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { useScreen } from '@/composables/useScreen'
import { apiPost } from '@/lib/http'
import { currentUser } from '@/lib/currentUser'
import { useStaticAsset } from '@/composables/useStaticAsset'

const asset = useStaticAsset()

interface TutorOffer {
  offer_id: number
  visible: boolean
  accept_requests: boolean
  consult_price?: number
  verbal_analysis_price?: number
  analysis_price?: number
  software: number[]
  payment_methods: number[]
  accepts_bonus?: boolean
}

interface Context {
  tutor_offer?: TutorOffer | null
  tutor_software?: Array<{ id: number; name: string }>
  tutor_payment_methods?: Array<{ id: number; name: string }>
  online_teaching?: { native_speaker: boolean; using_pikimoni: boolean } | null
  [key: string]: unknown
}

const props = defineProps<{
  context: Context
  isCurrentUser: boolean
}>()

const { notify } = useNotification()
const { isMobile } = useScreen()

const tutorVisibilityList = [
  { value: 0, text: 'Принимать' },
  { value: 1, text: 'Не принимать' },
  { value: 2, text: 'Не принимать и скрыть анкету' }
]

const tutorOfferVisibility = ref(0)

const offer = props.context.tutor_offer
if (offer && offer.accept_requests) tutorOfferVisibility.value = 0
else if (offer && offer.visible) tutorOfferVisibility.value = 1
else tutorOfferVisibility.value = 2

const tutorSoftwareMap = computed(() => {
  const result: Record<number, string> = {}
  for (const { id, name } of props.context.tutor_software ?? []) {
    result[id] = name
  }
  return result
})

const tutorPaymentMethodsMap = computed(() => {
  const result: Record<number, string> = {}
  for (const { id, name } of props.context.tutor_payment_methods ?? []) {
    result[id] = name
  }
  return result
})

const tutorOfferPaymentMethods = computed(() => {
  const tutor = props.context.tutor_offer
  if (!tutor) return ''

  const paymentMethods = tutor.payment_methods.map(id => tutorPaymentMethodsMap.value[id])

  if (tutor.accepts_bonus) paymentMethods.unshift('Бонусы')

  return paymentMethods.join(', ')
})

const currentUserIsTeacher = computed(() => currentUser.isTeacher)

const slugify = (val?: string) => {
  if (!val) return ''
  return val.toLowerCase().replace(/\s/g, '-')
}

async function updateTutorVisibility() {
  const updateParams: { visible: boolean; accept_requests: boolean } = {
    visible: false,
    accept_requests: false
  }

  switch (tutorOfferVisibility.value) {
    case 0:
      updateParams.visible = true
      updateParams.accept_requests = true
      break
    case 1:
      updateParams.visible = true
      updateParams.accept_requests = false
      break
    default:
      updateParams.visible = false
      updateParams.accept_requests = false
  }

  try {
    const { failed, message } = await apiPost('/tutors/request_set_tutor_offer_state', updateParams)

    if (failed) throw message

    // if (props.context.tutor_offer) {
    //   props.context.tutor_offer.visible = updateParams.visible
    //   props.context.tutor_offer.accept_requests = updateParams.accept_requests
    // }
  } catch (err) {
    let title = 'Произошла ошибка при изменении статуса анкеты. Повторите попытку.'

    if (typeof err === 'string') title = err
    notify({ type: 'error', title })
  }
}
</script>

<template>
  <div class="d-flex flex-column">
    <div class="d-flex justify-space-between align-center mt-2">
      <h2 class="text-title-bold d-flex">Метод. консультации</h2>

      <v-menu-base v-if="isCurrentUser" pos-horizontal="right" :close-on-inner-click="false">
        <template #activator="{ onclick }">
          <v-link class="ml-2" @click="onclick">
            <v-icon class="gray-text" size="20">mdi-pencil</v-icon>
          </v-link>
        </template>

        <div class="pa-1">
          <v-radio-group
            v-model="tutorOfferVisibility"
            :items="tutorVisibilityList"
            @update:model-value="updateTutorVisibility"
          ></v-radio-group>

          <hr v-if="context.tutor_offer !== null" />

          <v-menu-button mode="primary" href="/tutors/edit_offer">
            <v-icon class="mr-1" size="20">mdi-file-edit-outline</v-icon>
            Изменить анкету
          </v-menu-button>
        </div>
      </v-menu-base>
    </div>

    <div v-if="!context.tutor_offer || !context.tutor_offer.accept_requests" class="my-2">
      Сейчас учитель не принимает заявки на консультации.
    </div>

    <div v-else-if="isMobile" class="d-flex flex-column">
      <div class="d-flex my-2">
        <div class="d-flex flex-column flex-spacer">
          <span class="gray-text mb-1">Консультация</span>
          <span class="text-title-bold">
            <template v-if="context.tutor_offer.consult_price">
              {{ context.tutor_offer.consult_price }}
              <v-icon class="ml-1">hand</v-icon>
            </template>
            <template v-else> (Нет) </template>
          </span>
        </div>

        <div class="mx-1"></div>

        <div class="d-flex flex-column flex-spacer">
          <span class="gray-text mb-1">Анализ урока</span>
          <span class="text-title-bold d-flex align-center">
            <template v-if="!context.tutor_offer.verbal_analysis_price && !context.tutor_offer.analysis_price">
              (Нет)
            </template>

            <template v-if="context.tutor_offer.verbal_analysis_price">
              {{ context.tutor_offer.verbal_analysis_price }}
              <v-icon class="ml-1">hand</v-icon>
            </template>

            <div
              v-if="context.tutor_offer.verbal_analysis_price && context.tutor_offer.analysis_price"
              class="mx-1"
            ></div>

            <template v-if="context.tutor_offer.analysis_price">
              {{ context.tutor_offer.analysis_price }}
              <v-icon class="ml-1">hand</v-icon>
            </template>
          </span>

          <span class="gray-text text-caption">
            <template v-if="context.tutor_offer.verbal_analysis_price && context.tutor_offer.analysis_price">
              Устный и письменный
            </template>
            <template v-else-if="context.tutor_offer.verbal_analysis_price"> Устный анализ </template>
            <template v-else-if="context.tutor_offer.analysis_price"> Письменный анализ </template>
          </span>
        </div>
      </div>

      <div class="d-flex my-2">
        <div class="d-flex flex-column flex-spacer">
          <span class="gray-text mb-1">Носитель языка</span>
          <span v-if="!context.online_teaching?.native_speaker" class="text-title-bold"> Нет </span>
          <template v-else>
            <span class="text-title-bold">Есть</span>
            <span class="text-caption">Привлеченный</span>
          </template>
        </div>

        <div class="mx-1"></div>

        <div class="d-flex flex-column flex-spacer">
          <span class="gray-text mb-1">Pikimoni</span>
          <span v-if="!context.online_teaching?.using_pikimoni" class="text-title-bold"> Нет </span>
          <template v-else>
            <span class="text-title-bold">Есть</span>
            <img :src="asset('img/pikimoni.svg')" height="32" />
          </template>
        </div>
      </div>

      <span class="gray-text mb-1 mt-2"> Программы </span>

      <div v-for="softwareId in context.tutor_offer.software" :key="softwareId" class="d-flex align-center my-half">
        <v-icon class="mr-1">soft-{{ slugify(tutorSoftwareMap[softwareId]) }}</v-icon>
        {{ tutorSoftwareMap[softwareId] }}
      </div>

      <span class="text-caption gray-text"> Подойдет одна из этих программ </span>
    </div>

    <table v-else class="teacher-bio__tariffs-table mt-2">
      <tbody>
        <tr>
          <th>Консультация</th>
          <th>Анализ урока</th>
          <th>Программы</th>
        </tr>

        <tr>
          <td>
            <template v-if="context.tutor_offer.consult_price">
              {{ context.tutor_offer.consult_price }}
              <v-icon class="ml-1">hand</v-icon>
            </template>
            <template v-else> (Нет) </template>
          </td>

          <td>
            <span class="text-title-bold d-flex align-center">
              <template v-if="!context.tutor_offer.verbal_analysis_price && !context.tutor_offer.analysis_price">
                (Нет)
              </template>

              <template v-if="context.tutor_offer.verbal_analysis_price">
                {{ context.tutor_offer.verbal_analysis_price }}
                <v-icon class="ml-1">hand</v-icon>
              </template>

              <div
                v-if="context.tutor_offer.verbal_analysis_price && context.tutor_offer.analysis_price"
                class="mx-1"
              ></div>

              <template v-if="context.tutor_offer.analysis_price">
                {{ context.tutor_offer.analysis_price }}
                <v-icon class="ml-1">hand</v-icon>
              </template>
            </span>

            <span class="gray-text text-caption">
              <template v-if="context.tutor_offer.verbal_analysis_price && context.tutor_offer.analysis_price">
                Устный и письменный
              </template>
              <template v-else-if="context.tutor_offer.verbal_analysis_price"> Устный анализ </template>
              <template v-else-if="context.tutor_offer.analysis_price"> Письменный анализ </template>
            </span>
          </td>

          <td>
            <div class="text-body d-flex flex-column">
              <div
                v-for="softwareId in context.tutor_offer.software"
                :key="softwareId"
                class="d-flex align-center my-half"
              >
                <v-icon class="mr-1">soft-{{ slugify(tutorSoftwareMap[softwareId]) }}</v-icon>
                {{ tutorSoftwareMap[softwareId] }}
              </div>
            </div>
          </td>
        </tr>

        <tr>
          <td colspan="5">
            <span class="text-caption gray-text">Способы оплаты</span><br />
            <span class="text-body">{{ tutorOfferPaymentMethods }}</span>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="d-flex mt-2">
      <div v-if="!isMobile" class="flex-spacer"></div>

      <v-button
        v-if="!isCurrentUser && context.tutor_offer && currentUserIsTeacher"
        mode="primary filled"
        :href="`/tutors/create_request/${context.tutor_offer.offer_id}`"
        target="_blank"
      >
        Записаться на консультацию
      </v-button>
    </div>
  </div>
</template>
